import React, { useEffect, useState } from "react";
import { Table } from "components/Common/Table/Table";
import ContractTableRow from "./ContractTableRow";
import DocumentPageHeader from "components/document/DocumentPageHeader";
import { PageFilterPanel } from "components/Common/PageFilterPannel";
import Chip from "components/foundation/Chip/Chip";
import FilterSearch from "components/Common/PageFilterPannel/FilterSearch";
import FilterRegion from "components/Common/PageFilterPannel/FilterRegion";
import Pagination from "components/foundation/Pagination";
import styled, { css } from "styled-components";
import Board from "components/Common/Board/Board";
import useMediaView from "features/common/hooks/useMediaView";
import useCategoryColumnsQuery from "features/document/hooks/queries/useCategoryColumnsQuery";
import useDocumentsAutoCompleteQuery from "features/document/hooks/queries/useDocumentsAutoCompleteQuery";
import useDocumentsQuery from "features/document/hooks/queries/useDocumentsQuery";
import { FormProvider, useForm } from "react-hook-form";
import { SearchIcon, TagIcon, DateIcon, ReloadIcon } from "assets/icons";
import TableDatePickerMobile from "components/Common/Table/TableDatePickerMobile";
import TableInputMobile, {
  TableInputMobileButton,
} from "components/Common/Table/TableInputMobile";
import TableTagInputMobile, {
  SelectedTags,
} from "components/Common/Table/TableTagInputMobile";
import { useGetDocumentTagsQuery } from "features/document/hooks/queries/useGetDocumentTagsQuery";
import MobileSelectedFilter, {
  ParamStateSetter,
} from "components/document/MobileSelectedFilter/MobileSelectedFilter";
import { produce } from "immer";
import DateRangePicker from "components/foundation/DateRangePicker";
import FilterListItem from "components/Common/PageFilterPannel/FilterListItem";
import removeEmptyFields from "utils/removeEmptyFields";
import SearchEmpty from "components/Common/Empty/SearchEmpty";

import dayjs from "dayjs";
import { useParamState } from "@yoonhaemin-lib/use-param-state";
import { LOCAL_TIME_FORMAT } from "config/constant";
import CheckBox from "components/foundation/CheckBox/CheckBox";
import downloadPDF from "utils/downloadPdf";
import Select from "components/foundation/Select/Select";
import Button from "components/foundation/Button";

interface FormData {
  searchType: string;
  search: string;
}

const isNotSelectedRegion = (
  tag: any,
): tag is { title: string; value: string }[] => {
  return Array.isArray(tag);
};

const ANALYSIS_UUID = "7e0b03c2-ac8d-11ef-a518-02b012ed9399";

const DocumentAnalysisDashboardPage = () => {
  const methods = useForm<FormData>({
    defaultValues: {
      searchType: "projectName",
      search: "",
    },
  });

  const isSmall = useMediaView("small");
  const [{ page, ...searchOptions }, setSearchOptions] = useParamState({
    searchType: "projectName",
    search: "",
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
    tags: undefined as SelectedTags | undefined,
    page: 1,
    size: 10,
  });

  const { data: columnsData } = useCategoryColumnsQuery({
    categoryUuid: ANALYSIS_UUID,
    isMobile: isSmall,
    listType: "LIST",
  });

  const { region, ...restField } = searchOptions.tags ?? {};

  const { data: documentsData } = useDocumentsQuery(
    removeEmptyFields({
      categoryUuid: ANALYSIS_UUID,
      ...(searchOptions.search
        ? { [searchOptions.searchType]: searchOptions.search }
        : {}),
      page: page - 1,
      size: searchOptions.size,
      startDate: searchOptions?.startDate
        ? dayjs(searchOptions.startDate).format(LOCAL_TIME_FORMAT)
        : undefined,
      endDate: searchOptions?.endDate
        ? dayjs(searchOptions.endDate).format(LOCAL_TIME_FORMAT)
        : undefined,
      provinceName: region?.region,
      cityName: region?.city,
      tagUuids: Object.values(restField)
        .filter(isNotSelectedRegion)
        .flatMap((tag) => tag.map((item) => item.value)),
    }),
  );

  const { data: tags } = useGetDocumentTagsQuery({
    categoryUuid: ANALYSIS_UUID,
  });

  const handleSearch = (data: FormData) => {
    setSearchOptions((pre) => ({ ...pre, ...data }));
  };

  const [isReset, setIsReset] = useState<boolean>();
  const handleSearchReset = () => {
    setSearchOptions((pre) => ({
      ...pre,
      search: "",
      startDate: "",
      endDate: "",
      page: 1,
    }));

    methods.setValue("search", "");
    methods.setValue("searchType", "projectName");

    setIsReset(!isReset);
  };

  const documents = documentsData.data.result.contents;

  const { data: autoCompleteData } = useDocumentsAutoCompleteQuery({
    type: methods.watch("searchType"),
    categoryUuid: ANALYSIS_UUID,
    keyword: methods.watch("search"),
  });

  const OPTIONS = tags.map((tag) => ({
    title: tag.name,
    value: tag.uuid,
    type: "checkbox" as const,
    options: tag.tagList.map((t) => ({
      title: t.name,
      value: t.uuid,
    })),
  }));

  const handleTagClick = ({
    category,
    tagId,
    tagTitle,
  }: {
    category: string;
    tagId: string;
    tagTitle: string;
  }) => {
    setSearchOptions((pre) =>
      produce(pre, (draft) => {
        draft.tags = draft.tags || {};

        // 기존 태그 배열 가져오기
        const targetCategory = draft.tags[category] || [];

        // 태그 업데이트 함수
        const updateTags = (tags: typeof targetCategory, id: typeof tagId) =>
          tags.some((tag) => tag.value === id)
            ? tags.filter((tag) => tag.value !== id) // 중복 시 제거
            : [...tags, { title: tagTitle, value: id }]; // 중복 없으면 추가

        // 선언형 업데이트
        draft.tags[category] = updateTags(targetCategory, tagId);
      }),
    );
  };

  // 전체 선택 체크박스 상태 관리
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);

  // 전체 선택 체크박스 클릭 이벤트
  const handleHeaderCheckBoxClick = () => {
    if (isAllSelected) {
      // 전체 해제
      setSelectedItems(new Set());
      setIsAllSelected(false);
    } else {
      // documentFileUrl이 있는 항목만 전체 선택
      const availableItems = documents
        .filter((doc) => doc.documentFileUrl)
        .map((doc) => doc.uuid);
      setSelectedItems(new Set(availableItems));
      setIsAllSelected(true);
    }
  };

  // 하위 체크박스 클릭 이벤트
  const handleRowCheckBoxClick = (documentFileUrl?: string) => {
    if (!documentFileUrl) return;

    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(documentFileUrl)) {
        newSet.delete(documentFileUrl);
      } else {
        newSet.add(documentFileUrl);
      }

      // 전체 선택 상태 업데이트
      const availableItems = documents.filter((doc) => doc.documentFileUrl);
      setIsAllSelected(
        availableItems.length > 0 &&
          availableItems.every((doc) => newSet.has(doc.documentFileUrl)),
      );

      return newSet;
    });
  };

  // 체크박스 선택 항목 초기화
  useEffect(() => {
    setSelectedItems(new Set());
    setIsAllSelected(false);
  }, [
    searchOptions.search,
    searchOptions.startDate,
    searchOptions.endDate,
    JSON.stringify(searchOptions.tags),
    page,
    searchOptions.size,
  ]);

  // pdf 다운로드 버튼으로 일괄 다운로드
  const handleDownloadPdf = () => {
    const selectedDocuments = documents.filter(
      (doc) => selectedItems.has(doc.uuid) && doc.documentFileUrl,
    );

    selectedDocuments.forEach((doc) => {
      downloadPDF(
        doc.documentFileUrl!,
        doc.additionalFields.analysisPJ || "document",
      );
    });
  };

  return (
    <DocumentAnalysisDashboardPageLayout>
      {!isSmall && (
        <>
          <DocumentPageHeader
            title="ANALYSIS"
            onFilterResetClick={() =>
              setSearchOptions((pre) => ({
                ...pre,
                tags: undefined,
              }))
            }
          />

          <form onSubmit={methods.handleSubmit(handleSearch)}>
            <FormProvider {...methods}>
              <PageFilterPanel.Board>
                <FilterRegion
                  region={region}
                  onChangeRegion={(region) => {
                    setSearchOptions((pre) => ({
                      ...pre,
                      tags: {
                        ...pre.tags,
                        region,
                      } as SelectedTags,
                    }));
                  }}
                />

                {OPTIONS.map((option) => (
                  <PageFilterPanel.ListItem
                    title={option.title}
                    key={option.title}
                  >
                    {option.options.map((o) => (
                      <Chip
                        type="button"
                        key={o.value}
                        isSelected={
                          searchOptions.tags?.[option.value]?.some(
                            (tag) => tag.value === o.value,
                          ) || false
                        }
                        onClick={() =>
                          handleTagClick({
                            category: option.value,
                            tagId: o.value,
                            tagTitle: o.title,
                          })
                        }
                      >
                        {o.title}
                      </Chip>
                    ))}
                  </PageFilterPanel.ListItem>
                ))}
                <PageFilterPanel.NestItem>
                  <FilterListItem title="등록일">
                    <DateRangePicker
                      defaultValue={{
                        start: searchOptions.startDate ?? "",
                        end: searchOptions.endDate ?? "",
                      }}
                      onChangeDate={({ start, end }) => {
                        setSearchOptions((pre) => ({
                          ...pre,
                          startDate: start.toString(),
                          endDate: end?.toString(),
                        }));
                      }}
                      onClickReset={isReset}
                    />
                  </FilterListItem>
                  <FilterSearch
                    dropdownList={[
                      {
                        label: "PJ명",
                        value: "projectName",
                      },
                      {
                        label: "자유태그",
                        value: "freeTag",
                      },
                      {
                        label: "작성자",
                        value: "author",
                      },
                    ]}
                    autocomplete={autoCompleteData?.data.result.results.map(
                      (i) => ({
                        label: i,
                        value: i,
                      }),
                    )}
                    onSearchReset={handleSearchReset}
                  />
                </PageFilterPanel.NestItem>
              </PageFilterPanel.Board>
            </FormProvider>
          </form>
        </>
      )}

      {isSmall && searchOptions.tags && (
        <MobileSelectedFilter
          tags={searchOptions.tags}
          setSearchOptions={
            setSearchOptions as ParamStateSetter<{
              searchType: string;
              search: string;
              startDate: string | undefined;
              endDate: string | undefined;
              tags: SelectedTags | undefined;
              page: number;
            }>
          }
        />
      )}

      <ContentBoard>
        <Table.Header>
          <Table.TotalCount>
            총 {documentsData.data.result.totalElements}건
          </Table.TotalCount>
          <Button
            variant="tertiary"
            size="small"
            className="pc-only"
            onClick={handleDownloadPdf}
            disabled={selectedItems.size === 0}
          >
            PDF 다운로드
          </Button>

          {isSmall && (
            <form onSubmit={methods.handleSubmit(handleSearch)}>
              <FormProvider {...methods}>
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    justifyContent: "end",
                  }}
                >
                  <TableInputMobile
                    searchType={[
                      {
                        label: "PJ명",
                        value: "projectName",
                      },
                      {
                        label: "자유태그",
                        value: "freeTag",
                      },
                      {
                        label: "작성자",
                        value: "author",
                      },
                    ]}
                    autocomplete={autoCompleteData?.data.result.results.map(
                      (i) => ({
                        label: i,
                        value: i,
                      }),
                    )}
                  >
                    <SearchIcon />
                    검색
                  </TableInputMobile>
                  <TableTagInputMobile
                    data={OPTIONS.map((i) => ({ ...i, label: i.title }))}
                    isRegion
                    initialValue={searchOptions.tags}
                    onSubmit={(value) => {
                      setSearchOptions((pre) => ({
                        ...pre,
                        tags: value,
                      }));
                    }}
                  >
                    <TagIcon />
                    태그
                  </TableTagInputMobile>
                  <TableDatePickerMobile
                    isSelected={!!searchOptions.startDate}
                    onDateRangeSelected={({ start, end }) => {
                      setSearchOptions((pre) => ({
                        ...pre,
                        startDate: start.toString(),
                        endDate: end?.toString(),
                      }));
                    }}
                  >
                    <DateIcon />
                    등록일
                  </TableDatePickerMobile>
                  <TableInputMobileButton
                    variant="tertiary"
                    size="small"
                    onClick={() => {
                      setSearchOptions({
                        searchType: "freeTag",
                        search: "",
                        startDate: undefined,
                        endDate: undefined,
                        tags: undefined,
                        page: 1,
                        size: 10,
                      });
                      methods.reset({
                        search: "",
                      });
                    }}
                  >
                    <ReloadIcon />
                    초기화
                  </TableInputMobileButton>
                </div>
              </FormProvider>
            </form>
          )}
        </Table.Header>

        {documentsData.data.result.totalElements === 0 && <SearchEmpty />}

        {documentsData.data.result.totalElements !== 0 && (
          <>
            <Table.Layout>
              <Table.Table
                {...(isSmall
                  ? { columns: "1fr auto auto auto", width: "100%" }
                  : {
                      columns:
                        "6.4rem auto auto auto minmax(0, 1fr) auto auto auto auto",
                    })}
              >
                <Table.Thead>
                  <Table.Th
                    className="pc-only"
                    width="6.4rem"
                    style={{ padding: "1.4rem 0", textAlign: "center" }}
                  >
                    <CheckBox
                      onClick={handleHeaderCheckBoxClick}
                      checked={isAllSelected}
                    />
                  </Table.Th>
                  {columnsData.data.result.map((column) => (
                    <Table.Th key={column.keyName}>{column.name}</Table.Th>
                  ))}
                  <Table.Th>문서 pdf</Table.Th>
                </Table.Thead>
                <Table.Tbody>
                  {documents.map((document, index) => (
                    <ContractTableRow
                      id={document.uuid}
                      key={document.uuid}
                      no={
                        documentsData.data.result.totalElements -
                        (index + (page - 1) * 10)
                      }
                      pj={document.additionalFields.analysisPJ}
                      businessType={document.additionalFields.businessCategory}
                      informationProvider={
                        document.additionalFields.informationProvider
                      }
                      author={document.author}
                      submissionDate={document.createdAt}
                      documentStatus={document.additionalFields.documentStatus}
                      documentFileUrl={document.documentFileUrl}
                      isSelected={selectedItems.has(document.uuid)}
                      onCheckBoxClick={handleRowCheckBoxClick}
                    />
                  ))}
                </Table.Tbody>
              </Table.Table>
            </Table.Layout>

            <TableFooter>
              <SelectContainer className="pc-only">
                <SelectLabel>페이지당 행</SelectLabel>
                <Select
                  list={[
                    { label: "10", value: "10" },
                    { label: "20", value: "20" },
                    { label: "50", value: "50" },
                  ]}
                  defaultValue="10"
                  onValueChange={(value) => {
                    setSearchOptions((pre) => ({
                      ...pre,
                      size: Number(value),
                    }));
                  }}
                />
              </SelectContainer>
              <Pagination
                currentPage={page}
                lastPage={documentsData.data.result.totalPages}
                onClickPageButton={(page) =>
                  setSearchOptions((pre) => ({ ...pre, page: page }))
                }
                style={
                  isSmall
                    ? { margin: "auto" }
                    : {
                        marginLeft: "auto",
                      }
                }
              />
            </TableFooter>
          </>
        )}
      </ContentBoard>
    </DocumentAnalysisDashboardPageLayout>
  );
};

export default DocumentAnalysisDashboardPage;

const ContentBoard = styled(Board)`
  padding: 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${({ theme }) => css`
    ${theme.media.small} {
      padding: 2rem 1.6rem;
    }
  `}
`;

const DocumentAnalysisDashboardPageLayout = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.25rem;
  align-items: center;
`;

const SelectLabel = styled.div`
  ${({ theme }) => theme.typography.body3.medium};
  color: ${({ theme }) => theme.color.text.secondary};
`;

const TableFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
